import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type { ModuleView, ProjectModuleView } from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Drawer,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  LoadingState,
  Select,
  StatusBadge,
} from "../../shared/ui";

/**
 * What this project is built from, and which version of each it runs.
 *
 * <p>The versions here are the starting point for every release: a new release opens with each
 * module at whatever this project is on today, and somebody then says which ones move. Without
 * this list a release has nothing to be a release of.
 */
export function ProjectModulesTab({ projectId }: { projectId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const held = useQuery({
    queryKey: ["project-modules", projectId],
    queryFn: () => api.get<ProjectModuleView[]>(`/api/v1/projects/${projectId}/modules`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["project-modules", projectId] });
    void queryClient.invalidateQueries({ queryKey: ["modules"] });
  };

  const unlink = async (moduleId: string) => {
    setBusy(moduleId);
    setError(null);
    try {
      await api.del(`/api/v1/projects/${projectId}/modules/${moduleId}`);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not remove that module.");
    } finally {
      setBusy(null);
    }
  };

  const rows = held.data ?? [];
  const behind = rows.filter((row) => !row.onLatest);

  return (
    <Card>
      <CardHeader
        title="Modules"
        subtitle={
          rows.length === 0
            ? "Nothing linked yet."
            : `${rows.length} ${rows.length === 1 ? "module" : "modules"}, at the versions this project is running.`
        }
        action={
          can("MODULE_MANAGE") && (
            <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setLinking(true)}>
              Add module
            </Button>
          )
        }
      />
      <CardBody className="space-y-3">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {behind.length > 0 && (
          <InlineAlert tone="warning">
            {behind.map((row) => row.module.name).join(", ")}{" "}
            {behind.length === 1 ? "is" : "are"} behind the latest release. A shared module released
            for another customer does not deploy itself here.
          </InlineAlert>
        )}

        {held.isPending ? (
          <LoadingState label="Loading modules…" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No modules linked"
            message="Link the services this product is built from. Releases are then planned against them, one version per module."
            action={
              can("MODULE_MANAGE") ? (
                <Button onClick={() => setLinking(true)}>Add the first one</Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
            {rows.map((row) => (
              <li
                key={row.module.id}
                className="flex items-center justify-between gap-3 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink-900">{row.module.name}</p>
                  <p className="text-xs text-ink-500">
                    <span className="font-mono">{row.module.code}</span>
                    {row.module.moduleType.sharedVersion && ` · ${row.module.moduleType.name}, shared`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="tnum font-mono text-sm text-ink-800">
                    {row.currentVersion ?? "—"}
                  </span>
                  {row.module.moduleType.sharedVersion && (
                    <StatusBadge tone={row.onLatest ? "green" : "amber"}>
                      {row.onLatest ? "Current" : `Latest is ${row.module.latestVersion}`}
                    </StatusBadge>
                  )}
                  {can("MODULE_MANAGE") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={busy === row.module.id}
                      icon={<Trash2 className="h-4 w-4" />}
                      aria-label={`Remove ${row.module.name}`}
                      onClick={() => void unlink(row.module.id)}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardBody>

      <LinkModuleDrawer
        projectId={projectId}
        open={linking}
        held={rows}
        onClose={() => setLinking(false)}
        onSaved={refresh}
      />
    </Card>
  );
}

function LinkModuleDrawer({
  projectId,
  open,
  held,
  onClose,
  onSaved,
}: {
  projectId: string;
  open: boolean;
  held: ProjectModuleView[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [moduleId, setModuleId] = useState("");
  const [currentVersion, setCurrentVersion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const modules = useQuery({
    queryKey: ["modules", "offered"],
    queryFn: () => api.get<ModuleView[]>("/api/v1/modules"),
    enabled: open,
  });

  const alreadyHeld = new Set(held.map((row) => row.module.id));
  const available = (modules.data ?? []).filter((module) => !alreadyHeld.has(module.id));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/projects/${projectId}/modules`, {
        moduleId,
        currentVersion: currentVersion.trim() === "" ? null : currentVersion.trim(),
      });
      onSaved();
      onClose();
      setModuleId("");
      setCurrentVersion("");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not add that module.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Add a module"
      subtitle="At the version this project is running today."
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={moduleId === ""} loading={saving}>
            {saving ? "Adding…" : "Add module"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {available.length === 0 && !modules.isPending ? (
          <EmptyState
            title="Nothing left to add"
            message="Every offered module is already linked. New ones are created under Administration → Modules."
          />
        ) : (
          <>
            <Field label="Module" required>
              <Select value={moduleId} onChange={(event) => setModuleId(event.target.value)}>
                <option value="">Choose a module…</option>
                {available.map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.name}
                    {module.moduleType.sharedVersion ? " (shared)" : ""}
                  </option>
                ))}
              </Select>
            </Field>

            {/*
              Asked rather than defaulted to the module's latest. A customer adopting Accounting
              today is usually put on whatever they were sold, and filling in last night's release
              would record a deployment nobody did.
            */}
            <Field
              label="Version this project is on"
              hint="What is actually running here. Leave blank if it has never been deployed for this project."
            >
              <Input
                value={currentVersion}
                placeholder="2.0"
                onChange={(event) => setCurrentVersion(event.target.value)}
              />
            </Field>
          </>
        )}
      </div>
    </Drawer>
  );
}
